import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GeneratedElfLetter } from "@/types/elf-letter";
import { cn } from "@/lib/utils";
import { hashEmail } from "@/utils/hashEmail";
import { getEmailSyntaxResult, suggestEmailAddress } from "@/utils/emailValidation";
import { Link } from "react-router-dom";
import { MagicLetterBoxLink } from "@/components/funnel/MagicLetterBoxLink";
import { FAMILY_OFFER } from "../../../shared/magic-letter-box";

interface EmailCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
  letter: GeneratedElfLetter | null;
  isSubmitting: boolean;
  error: string | null;
  warning?: string | null;
  remainingFreeLetters: number | null;
  usageMap: Record<string, number>;
  maxFreeLetters: number;
}

const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({
  open,
  onClose,
  onSubmit,
  letter,
  isSubmitting,
  error,
  warning,
  remainingFreeLetters,
  usageMap,
  maxFreeLetters,
}) => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [usageCount, setUsageCount] = useState<number | null>(null);
  const [suggestionPrompt, setSuggestionPrompt] = useState<{
    entered: string;
    suggested: string;
  } | null>(null);
  const [suggestionDecisionFor, setSuggestionDecisionFor] = useState<string | null>(
    null,
  );
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setEmail(letter?.email ?? "");
      setTouched(false);
      setUsageCount(null);
      setSuggestionPrompt(null);
      setSuggestionDecisionFor(null);
    } else if (letter?.email) {
      setEmail(letter.email);
    }
  }, [open, letter]);

  const validationMessage = useMemo(() => {
    return getEmailSyntaxResult(email).message ?? null;
  }, [email]);

  const submitAddress = async (address: string) => {
    if (submitLockRef.current || isSubmitting) return;
    submitLockRef.current = true;
    const normalizedAddress = address.trim().toLowerCase();
    setEmail(normalizedAddress);
    setSuggestionDecisionFor(normalizedAddress);
    setSuggestionPrompt(null);
    try {
      await onSubmit(normalizedAddress);
    } finally {
      submitLockRef.current = false;
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (validationMessage || isSubmitting || submitLockRef.current) return;

    const enteredAddress = email.trim().toLowerCase();
    const suggestedAddress = suggestEmailAddress(enteredAddress);
    if (suggestedAddress && suggestionDecisionFor !== enteredAddress) {
      setSuggestionPrompt({ entered: enteredAddress, suggested: suggestedAddress });
      return;
    }

    await submitAddress(enteredAddress);
  };

  useEffect(() => {
    let cancelled = false;

    const evaluateUsage = async () => {
      if (!open) {
        setUsageCount(null);
        return;
      }
      const normalized = email.trim().toLowerCase();
      if (!normalized) {
        setUsageCount(null);
        return;
      }
      try {
        const hash = await hashEmail(normalized);
        if (!cancelled) {
          setUsageCount(usageMap[hash] ?? null);
        }
      } catch {
        if (!cancelled) {
          setUsageCount(null);
        }
      }
    };

    void evaluateUsage();

    return () => {
      cancelled = true;
    };
  }, [email, usageMap, open]);

  const usageRemaining =
    usageCount != null
      ? Math.max(0, maxFreeLetters - usageCount)
      : null;

  const effectiveRemaining =
    remainingFreeLetters != null ? remainingFreeLetters : usageRemaining;

  const remainingMessage =
    effectiveRemaining != null
      ? effectiveRemaining > 0
        ? `You have ${effectiveRemaining} of ${maxFreeLetters} free letters remaining.`
        : "You've used all of your free elf letters for this email address."
      : `You can send up to ${maxFreeLetters} free letters per email address.`;

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-y-auto sm:w-full rounded-3xl border border-muted bg-gradient-to-br from-white via-elfGreen-light/40 to-white shadow-2xl p-4 sm:p-6 [&>button:focus]:ring-slate-700 [&>button:focus]:opacity-100">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-foreground">
            Email Your Elf Letter
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
            Enter an email address and we&apos;ll send a printable PDF within a few
            seconds.
          </DialogDescription>
        </DialogHeader>

        <form className="mt-4 sm:mt-6 space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="delivery-email">Email Address</Label>
            <Input
              id="delivery-email"
              type="email"
              required
              autoComplete="email"
              disabled={isSubmitting}
              value={email}
              onChange={(event) => {
                setEmail(event.currentTarget.value);
                setSuggestionPrompt(null);
                setSuggestionDecisionFor(null);
                if (!touched) setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              placeholder="parent@email.com"
              className={cn(
                "h-11 sm:h-12 rounded-xl border-muted focus-visible:ring-slate-700 text-base",
                touched && validationMessage && "border-destructive",
              )}
            />
            <p className="text-xs text-muted-foreground">{remainingMessage}</p>
            <p className="text-xs leading-relaxed text-muted-foreground">We use your email to deliver the PDF through our email provider and store it with delivery records to manage the limit of three free PDF deliveries per email address.{" "}<Link to="/privacy/" className="underline underline-offset-4">Privacy details</Link></p>
            {touched && validationMessage && (
              <p className="text-xs text-elfRed-ink">{validationMessage}</p>
            )}
            {suggestionPrompt && !validationMessage && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950">
                <p>
                  Did you mean <strong>{suggestionPrompt.suggested}</strong>?
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="bg-elfGreen-ink text-white hover:bg-[#1b422c] focus-visible:ring-slate-700"
                    disabled={isSubmitting}
                    onClick={() => void submitAddress(suggestionPrompt.suggested)}
                  >
                    Use suggestion
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="focus-visible:ring-slate-700"
                    disabled={isSubmitting}
                    onClick={() => void submitAddress(suggestionPrompt.entered)}
                  >
                    Keep entered address
                  </Button>
                </div>
              </div>
            )}
            {error && <p className="text-xs text-elfRed-ink">{error}</p>}
            {warning && <p className="text-xs text-muted-foreground">{warning}</p>}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 sm:h-12 rounded-xl bg-elfRed-ink hover:bg-[#892638] text-white font-semibold tracking-wide transition text-sm sm:text-base focus-visible:ring-slate-700"
          >
            {isSubmitting ? "Sending..." : "Send My Letter"}
          </Button>
          <p className="text-[11px] leading-relaxed text-muted-foreground">By requesting your free letter, you’ll also receive occasional holiday emails from The Magic Letter Box. Unsubscribe anytime.{" "}<Link to="/terms/" className="underline underline-offset-4">Terms</Link></p>
        </form>

        <div className="mt-4 sm:mt-6 rounded-2xl bg-white/85 p-3 sm:p-4 text-xs sm:text-sm shadow-inner">
          <h4 className="font-semibold text-foreground text-sm sm:text-base">
            Want unlimited magical letters?
          </h4>
          <p className="mt-1.5 sm:mt-1 text-muted-foreground leading-relaxed">
            <MagicLetterBoxLink
              source="generator-email-modal"
              className="rounded underline underline-offset-4 text-elfGreen-ink hover:text-[#1b422c] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
            >
              The Magic Letter Box
            </MagicLetterBoxLink>{" "}
            Family membership is {FAMILY_OFFER.price} for unlimited letters, premium templates, and up to {FAMILY_OFFER.children} children. Start with {FAMILY_OFFER.trialLetters} free letters in your new account.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCaptureModal;

